import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useNavigate, useLocation, Link } from "react-router-dom";
import axiosInstance from "../api/axiosInstance";
import usePagination from "../hooks/usePagination";
import LoadingAnimation from "../components/LoadingAnimation";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";
import { PhotoProvider, PhotoView } from "react-photo-view";
import "react-photo-view/dist/react-photo-view.css";
import Swal from "sweetalert2";
import "./CategoryProducts.css";

const LOGIN_PATH = "/login";
const SIGNUP_PATH = "/signup-customer";
const CATEGORY_MAP = {
  "health supplements": "Health supplements",
  "intimate care": "Intimate care",
  "skin and hair products": "Skin And Hair Products",
};
function decodeCategoryParam(value) {
  if (!value) return "";
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

export default function PublicCategoryProducts() {
  const { category: rawCategory } = useParams();
  const navigate = useNavigate();
  const location = useLocation();


const category = decodeCategoryParam(rawCategory);

const apiCategory =
  CATEGORY_MAP[category.trim().toLowerCase()] || category.trim();
  // Keep category routing compatible with URLs such as:
  // /public/Intimate%20care


const fetchProducts = useCallback(
  (page) =>
    axiosInstance.get("/consumer/by-category/", {
      params: {
        category: apiCategory,
        page,
        search: searchRef.current.trim() || undefined,
      },
    }),
  [apiCategory]
);

  const [search, setSearch] = useState("");
  const searchRef = useRef("");
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    searchRef.current = search;
    setRefreshKey((key) => key + 1);
  }, [search]);

  const {
    items: products,
    loading,
    reset,
  } = usePagination(fetchProducts, [category, refreshKey]);

  const [loadError, setLoadError] = useState("");
  const [retryKey, setRetryKey] = useState(0);

  useEffect(() => {
  setLoadError("");
  reset();
  // reset intentionally excluded: usePagination recreates this function
  // on every render.
}, [category, refreshKey, retryKey]);
  // Public category pages never read or mutate a guest's cart.
  const promptLogin = async (product) => {
    if (!product || Number(product.stock ?? 0) <= 0) return;

    const result = await Swal.fire({
      title: "Login to continue",
      text: "Please log in or create an account to add this item to your cart.",
      icon: "info",
      showCancelButton: true,
      confirmButtonText: "Login",
      cancelButtonText: "Sign Up",
      confirmButtonColor: "#2563eb",
      cancelButtonColor: "#16a34a",
      reverseButtons: true,
    });

    const returnTo = `${location.pathname}${location.search}`;
    if (result.isConfirmed) {
      navigate(LOGIN_PATH, { state: { from: returnTo } });
    } else if (result.dismiss === Swal.DismissReason.cancel) {
      navigate(SIGNUP_PATH, { state: { from: returnTo } });
    }
  };

  // Convert errors from the pagination hook into a visible retry state if the
  // hook exposes them in the future; current hook may only expose loading/items.
  const productsList = Array.isArray(products) ? products : [];

  return (
    <main
      style={{
        padding: 20,
        paddingTop: "calc(20px + env(safe-area-inset-top))",
        paddingBottom: "calc(24px + env(safe-area-inset-bottom))",
      }}
    >
      <section style={searchWrapper}>
        <input
          type="search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder={`Search ${category || "products"}...`}
          aria-label={`Search ${category || "products"}`}
          style={searchBox}
        />
      </section>

      {loading && productsList.length === 0 && <LoadingAnimation />}

      {!loading && productsList.length === 0 && (
        <section style={emptyState}>
          <p>No products found in {category || "this category"}.</p>
          <button type="button" style={retryButton} onClick={() => setRetryKey((key) => key + 1)}>
            Try again
          </button>
        </section>
      )}

      <section style={gridStyle} aria-label={`${category || "Category"} products`}>
        {productsList.map((product) => {
          const mrp = Number(String(product.mrp ?? 0).trim()) || 0;
          const sellingPrice =
            Number(String(product.selling_price ?? product.price ?? 0).trim()) || 0;
          const stock = Number(product.stock ?? 0);
          const discount =
            mrp > sellingPrice && mrp > 0
              ? Math.round(((mrp - sellingPrice) / mrp) * 100)
              : 0;

          const images =
            Array.isArray(product.images) && product.images.length
              ? product.images.filter(Boolean)
              : product.image
                ? [product.image]
                : [];

          return (
            <article key={product.id} style={card}>
              <h2 style={nameBox}>
  <Link
    to={`/product/${product.name
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "")}-${product.id}`}
    style={{ color: "inherit", textDecoration: "none" }}
  >
    {product.name}
  </Link>
</h2>

              <div style={imageFrame}>
                {images.length > 0 ? (
                  <PhotoProvider maskClosable>
                    <Swiper
                      modules={[Pagination]}
                      pagination={{ clickable: true }}
                      spaceBetween={10}
                      slidesPerView={1}
                      observer
                      observeParents
                      style={{ width: "100%", height: "100%" }}
                    >
                      {images.map((imageUrl, index) => (
                        <SwiperSlide key={`${product.id}-${index}`}>
                          <div style={imageSlide}>
                            <PhotoView src={imageUrl}>
                              <img
                                src={imageUrl}
                                alt={product.name || "Product"}
                                loading="lazy"
                                onError={(event) => {
                                  if (!event.currentTarget.dataset.fallback) {
                                    event.currentTarget.dataset.fallback = "true";
                                    event.currentTarget.src = "/no-image.png";
                                  }
                                }}
                                style={productImage}
                              />
                            </PhotoView>
                          </div>
                        </SwiperSlide>
                      ))}
                    </Swiper>
                  </PhotoProvider>
                ) : (
                  <div style={noImg}>No image available</div>
                )}
              </div>

              <p style={zoomHint}>Tap image to zoom • Tap outside to close</p>

              <div style={priceBox}>
                {mrp > sellingPrice && <del style={mrpStyle}>₹{mrp.toFixed(2)}</del>}
                <div style={priceStyle}>₹{sellingPrice.toFixed(2)}</div>
                {discount > 0 && <div style={offStyle}>{discount}% OFF</div>}
                {stock <= 0 && <div style={outOfStock}>Out of Stock</div>}
              </div>

              <button
                type="button"
                onClick={() => promptLogin(product)}
                disabled={stock <= 0}
                style={{
                  ...addBtn,
                  ...(stock <= 0 ? disabledAddBtn : {}),
                }}
              >
                {stock <= 0 ? "Out of Stock" : "Add to Cart"}
              </button>
            </article>
          );
        })}
      </section>

      {loading && productsList.length > 0 && (
        <div style={loadingMore}>
          <LoadingAnimation small />
        </div>
      )}
    </main>
  );
}

const searchWrapper = {
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  margin: "8px auto 20px",
};

const searchBox = {
  width: "min(100%, 420px)",
  minWidth: 220,
  padding: "11px 13px",
  borderRadius: 9,
  border: "1px solid #d1d5db",
  fontSize: 14,
  boxSizing: "border-box",
};

const gridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))",
  gap: 18,
};

const card = {
  border: "1px solid #e5e7eb",
  padding: 14,
  borderRadius: 12,
  background: "#fff",
  boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
  minHeight: 420,
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  boxSizing: "border-box",
};

const nameBox = {
  textAlign: "center",
  fontWeight: 700,
  fontSize: 14,
  lineHeight: 1.4,
  margin: "0 0 8px",
  minHeight: 38,
  color: "#1f2937",
};

const imageFrame = {
  height: 150,
  width: "100%",
  textAlign: "center",
  overflow: "hidden",
};

const imageSlide = {
  display: "flex",
  width: "100%",
  height: "100%",
  justifyContent: "center",
  alignItems: "center",
  overflow: "hidden",
};

const productImage = {
  width: "100%",
  height: "100%",
  objectFit: "contain",
  cursor: "zoom-in",
};

const noImg = {
  background: "#f8fafc",
  height: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "#94a3b8",
  fontSize: 12,
};

const zoomHint = {
  fontSize: 11,
  color: "#94a3b8",
  textAlign: "center",
  margin: "5px 0",
};

const priceBox = {
  textAlign: "center",
  marginTop: 8,
  fontWeight: 700,
};

const mrpStyle = {
  textDecoration: "line-through",
  color: "#dc2626",
  fontSize: 13,
  display: "block",
};

const priceStyle = {
  color: "#15803d",
  fontSize: 17,
};

const offStyle = {
  color: "#ea580c",
  fontSize: 13,
};

const outOfStock = {
  color: "#dc2626",
  fontSize: 12,
  fontWeight: 700,
};

const addBtn = {
  width: "100%",
  marginTop: 10,
  background: "#15803d",
  color: "#fff",
  padding: 10,
  borderRadius: 7,
  border: "none",
  fontWeight: 700,
  cursor: "pointer",
};

const disabledAddBtn = {
  background: "#e5e7eb",
  color: "#64748b",
  cursor: "not-allowed",
};

const retryButton = {
  padding: "9px 15px",
  border: 0,
  borderRadius: 8,
  background: "#2563eb",
  color: "#fff",
  fontWeight: 700,
  cursor: "pointer",
};

const emptyState = {
  padding: 24,
  color: "#64748b",
  textAlign: "center",
};

const loadingMore = {
  display: "flex",
  justifyContent: "center",
  padding: 18,
};
